/**
 * AXIS fit logic, lifted out of the advisor's lib/compatibility.ts so the same rules can
 * run on the Live2Kite storefront, in Node for validation, and later inside a Dawn
 * section. No framework, no TypeScript, no imports: it takes the rules object and plain
 * { title } products, so whatever calls it just supplies data.
 *
 * ONE RULE SET, NOT TWO. The advisor and the shop must never disagree about whether a
 * mast fits a fuselage; that is how the four incompatible product-code systems happened.
 * This reads public/data/compatibility-rules.json, the same file the advisor uses.
 *
 * TWO BUGS CARRIED OVER FROM THE ORIGINAL, FIXED HERE, and both are live in the AXIS
 * advisor today:
 *
 *   1. span was read with /\b(\d{3,4})\b/, and a trailing unit destroys the word
 *      boundary, so "AXIS Foils 1010mm Carbon Front Wing" returned NO span. Every
 *      span-dependent rule then silently passed: the Fireball 1500 Fatty warning, the
 *      K-Series 600 mm limit and the Kaiwi limits all went quiet on exactly the wings
 *      they exist to catch.
 *   2. the same pattern matched a MODEL YEAR: "AXIS Foils 2023 500 Freeride" read as a
 *      span of 2023, which is past every limit, so it would warn on everything.
 *
 * Verdict severities: ok, info (works, with a caveat worth saying), warn (AXIS would not
 * pair these), block (does not fit). Nothing guesses: an unknown part returns block with
 * "not in the fit table yet", because telling somebody a part fits when we do not know
 * sells them the wrong thing.
 */
'use strict';

// The rule patterns were authored against AXIS-store titles and several are anchored
// with ^ ("^red (standard|short|...)"). Live2Kite prefixes its own brand and often a
// model year, so "AXIS Foils Red Short Fuselage" never matched "^red short". Strip the
// brand prefix and a leading year before matching, so ONE rule set serves both stores
// instead of each store growing its own patterns.
const norm = (s) => String(s || '')
  .toLowerCase()
  .replace(/^\s*axis\s+foils\s+/, '')
  .replace(/^\s*axis\s+/, '')
  .replace(/^\s*(19|20)\d{2}\s+/, '')
  .replace(/^\s*\d{2}\/\d{2}\s+/, '')
  .trim();
const re = (p) => new RegExp(p, 'i');

// Years are not spans. No AXIS front wing span falls in 1900-2035; the largest is the
// Fireball 2100.
function frontWingSpan(p) {
  const t = String((p && p.title) || p || '');
  const all = [...t.matchAll(/\b(\d{3,4})\s*(?:mm|cm)?\b/gi)]
    .map((m) => Number(m[1]))
    .filter((n) => !(n >= 1900 && n <= 2035));
  return all.length ? all[0] : null;
}

function frontWingSystem(rules, title) {
  const t = norm(title);
  for (const f of rules.frontWingSystems) if (re(f.match).test(t)) return f.system;
  return null;
}
const familyBy = (list, title) => {
  const t = norm(title);
  for (const f of list) if (re(f.match).test(t)) return f;
  return null;
};
// 16mm masts and K-Series fuselages are discontinued (Evan, 2026-09-19) and are flagged
// in the rules so nothing offers them even if a stray product lingers.
const isDiscontinued = (fam) => !!(fam && fam.discontinued);
const fuselageFamily = (rules, title) => familyBy(rules.fuselageFamilies, title);
const rearFamily = (rules, title) => familyBy(rules.rearWingFamilies, title);
const mastFamily = (rules, title) => familyBy(rules.mastFamilies, title);

function ruleText(rules, id) {
  const r = rules.rules.find((x) => x.id === id);
  return (r && r.text) || id;
}

function fuselageFits(rules, front, fuselage) {
  const sys = frontWingSystem(rules, front.title);
  const fam = fuselageFamily(rules, fuselage.title);
  if (!sys) return { ok: false, severity: 'block', reasons: ['This front wing is not in the fit table yet.'] };
  if (!fam) return { ok: false, severity: 'block', reasons: ['This fuselage is not in the fit table yet.'] };
  const reasons = [];
  if (fam.system === 'kseries') {
    const span = frontWingSpan(front);
    if (span && span > (fam.maxFrontSpan || 600)) return { ok: false, severity: 'block', reasons: [ruleText(rules, 'kseries-span')] };
    return { ok: true, severity: 'ok', reasons: [] };
  }
  // Evan, 2026-09-19: "All Black fuselages (other than fatty) work with all front and
  // rear wings (except from the TI Link compatible ones)." That is broader than the
  // system-must-match rule this file shipped with, which came from the SKU sheet's
  // Fuselage Type column and was blocking Red-era wings on Black fuselages.
  if (fam.acceptsAllExceptLink) {
    if (sys === 'link') {
      return { ok: false, severity: 'block', reasons: [ruleText(rules, 'black-takes-all')] };
    }
  } else if (fam.system !== sys) {
    const want = rules.systems[sys] ? rules.systems[sys].label : sys;
    const got = rules.systems[fam.system] ? rules.systems[fam.system].label : fam.family;
    return { ok: false, severity: 'block', reasons: [`${want} wing; this is a ${got} fuselage.`] };
  }
  let severity = 'ok';
  const span = frontWingSpan(front) || 0;
  // Evan, 2026-09-27: the Fireball 1500 also runs on the Psychoshort Advance+ ("Psychoshort adv + or Fatty for the
  // 1500"), so only the 1750 and 2100 are warned off a fuselage that is not the FATTY.
  if (/fireball/i.test(front.title) && span >= 1500 && !/\b1500\b/.test(front.title) && !fam.fatty) { reasons.push(ruleText(rules, 'fireball-big-fatty')); severity = 'warn'; }
  if (fam.runout) { reasons.push(ruleText(rules, 'red-runout')); if (severity === 'ok') severity = 'info'; }
  return { ok: true, severity, reasons };
}

function rearFits(rules, fuselage, rear) {
  const fam = fuselageFamily(rules, fuselage.title);
  const rf = rearFamily(rules, rear.title);
  if (!fam || !rf) return { ok: false, severity: 'block', reasons: ['Not in the fit table yet.'] };
  if (fam.rearMount === 'link') {
    if (rf.mounts.includes('link')) return { ok: true, severity: 'ok', reasons: [] };
    if (rf.mounts.includes('link-adapter')) return { ok: true, severity: 'info', reasons: [ruleText(rules, 'link-rear')] };
    return { ok: false, severity: 'block', reasons: [ruleText(rules, 'link-rear')] };
  }
  if (rf.mounts.includes('standard')) return { ok: true, severity: 'ok', reasons: [] };
  return { ok: false, severity: 'block', reasons: [ruleText(rules, 'skinny-link-only-link')] };
}

function mastFits(rules, fuselage, mast, front, riderKg) {
  const fam = fuselageFamily(rules, fuselage.title);
  const mf = mastFamily(rules, mast.title);
  if (!fam || !mf) return { ok: false, severity: 'block', reasons: ['Not in the fit table yet.'] };
  const reasons = [];
  let severity = 'ok';
  if (mf.fits === 'fatty-only' && !fam.fatty) return { ok: false, severity: 'block', reasons: [ruleText(rules, 'fatty-mast-needs-fatty-fuse')] };
  // The 19mm aluminium colour does NOT decide which fuselage a mast fits (Evan,
  // 2026-09-20: "Both black 19mm and red 19mm masts work with both black fuses and red
  // fuses (and Ti Link)"). The colour matters only BETWEEN the aluminium parts, which is
  // the doodad and base plate pairing, handled where those parts are chosen. The one fit
  // consequence is the Fatty: it takes the black 19mm through the black Fatty adaptor,
  // so the red 19mm does not fit it.
  //
  // A first version of this read "black parts only with black" as a FUSELAGE rule and
  // refused red masts on black fuselages. There is no black system and red system here,
  // only black and red fuselages and black and red aluminium parts.
  if (mf.colour === 'red' && fam.fatty) {
    return { ok: false, severity: 'block', reasons: [ruleText(rules, 'red-19mm-not-fatty')] };
  }
  if (fam.fatty && mf.fits !== 'fatty-only') { reasons.push(ruleText(rules, 'fatty-fuse-mast')); severity = 'info'; }
  if (!mf.carbon && fam.system !== 'link') { reasons.push(ruleText(rules, 'alu-doodad')); if (severity === 'ok') severity = 'info'; }
  if (mf.requires) { reasons.push(`Needs a ${mf.requires}.`); if (severity === 'ok') severity = 'info'; }
  if (mf.limits && front) {
    const span = frontWingSpan(front) || 0;
    const tooBig = mf.limits.maxFrontSpan && span > mf.limits.maxFrontSpan;
    const tooHeavy = mf.limits.maxRiderKg && riderKg && riderKg > mf.limits.maxRiderKg;
    const wrongSeries = (mf.limits.notForSeries || []).some((s) => re(`\\b${s}\\b`).test(front.title));
    if (tooBig || tooHeavy || wrongSeries) { reasons.push(ruleText(rules, 'kaiwi-limits')); severity = 'warn'; }
    else if (mf.limits.specFrontSpan && span > mf.limits.specFrontSpan) { reasons.push(ruleText(rules, 'kaiwi-spec')); if (severity === 'ok') severity = 'info'; }
  }
  // Evan, 2026-09-27, on the Fireball 1500 without the FATTY: the PRO Ultra High Modulus 72 or 80 is "an amazing option
  // for that wing", the Power Carbon High Modulus 75 or 82 works, aluminium is an option, and "the regular power carbon
  // is not stiff enough". The 1750 and 2100 keep the FATTY warning. Either warning goes FIRST: the builder's rows show
  // only the first reason, and the doodad note used to hide it on aluminium masts.
  if (front && /fireball/i.test(front.title) && (frontWingSpan(front) || 0) >= 1500 && mf.fits !== 'fatty-only') {
    if (!/\b1500\b/.test(front.title)) { reasons.unshift(ruleText(rules, 'fireball-big-fatty')); severity = 'warn'; }
    else if (mf.family === 'Power Carbon') { reasons.unshift(ruleText(rules, 'fireball-1500-power-carbon')); severity = 'warn'; }
  }
  if (front && /png v2 1400/i.test(front.title) && riderKg && riderKg >= 95 && mf.fits !== 'fatty-only') { reasons.push(ruleText(rules, 'pngv2-1400-heavy')); severity = 'warn'; }
  return { ok: true, severity, reasons };
}

const API = { isDiscontinued, frontWingSpan, frontWingSystem, fuselageFamily, rearFamily, mastFamily, fuselageFits, rearFits, mastFits, ruleText };

if (typeof module !== 'undefined' && module.exports) module.exports = API;
if (typeof window !== 'undefined') window.AxisFit = API;
